import {memo, useCallback, useMemo, useState} from 'react';

import styled, {css} from 'styled-components';

import {flexCenterVertically} from '~/shared/theme/FlexLayout';
import ClickOutsideDiv, {ClickOutsideDivProps} from '~/shared/components/ClickOutsideDiv';
import {flipOnLTR, media} from '~/shared/theme/utils';
import {SupportedLanguages, DEFAULT_CULTURE, DEFAULT_LANGUAGE_KEY, EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {setCurrentLanguageCookie} from '~/shared/utils/cookies';
import Button from '~/shared/components/Button';
import {changeLanguagePrefixToUrlOrPath, getLocalizationService} from '~/shared/services/localisationService';
import {Link} from '~/shared/router';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {PIE_COUNTRY_ICONS} from '~/shared/consts/header';
import {usePreventBodyScrolling} from '~/shared/hooks';
import CheckedIcon from '~/assets/images/icons/checked-icon.svg?react';

type Variant = 'tenbis' | 'justeat';

// #region styled components
const Root = styled.div`
  display: flex;
`;

const changeButtonTenbisVariantStyle = css`
  height: 30px;
  padding: 0;
  ${flipOnLTR`
    margin-right: 20px;
    ${media.minDesktop`
      margin-right: 32px;
    `}
  `}
`;

const changeButtonJustEatVariantStyle = css`
  padding: 10px;
`;

const changeLangButtonVariantMap = {
  tenbis: changeButtonTenbisVariantStyle,
  justeat: changeButtonJustEatVariantStyle,
};

const ChangeLangButton = styled(Button)<{variant: Variant}>`
  direction: ltr;
  ${flexCenterVertically}
  ${({variant}) => changeLangButtonVariantMap[variant]}
  align-self: center;
`;

const circleFlagTenbisVariantStyle = css`
  border-width: 6px;
  border-style: solid;
  width: 32px;
  height: 32px;
`;

const circleFlagJustEactVariantStyle = css`
  padding: 1px;
  width: 22px;
  height: 22px;
`;

const circleFlagVariantMap = {
  tenbis: circleFlagTenbisVariantStyle,
  justeat: circleFlagJustEactVariantStyle,
};

const CircleFlag = styled(ImageWithAlt)<{isOpen?: boolean; variant: Variant}>`
  cursor: pointer;
  border-radius: 50%;
  ${({variant}) => circleFlagVariantMap[variant]}
  ${({theme, isOpen}) => `
    background-color: ${isOpen ? theme.colors.secondaryActive : theme.colors.gray920};
    border-color: ${isOpen ? theme.colors.secondaryActive : theme.colors.gray920};
  `}
`;

const ClickOutsideWithoutExtraProps = ({isOpen: _ignore, ...rest}: ClickOutsideDivProps & {
  isOpen: boolean;
}) => <ClickOutsideDiv {...rest} />;

const Dropdown = styled(ClickOutsideWithoutExtraProps).attrs({
  tabIndex: -1,
})`
  display: ${({isOpen}) => (isOpen ? 'block' : 'none')};
  width: 160px;
  position: absolute;
  top: 35px;
  ${flipOnLTR`
    left: 0px;
  `}
  padding: 10px 0;
  box-shadow: ${({theme}) => theme.shadows.shadow2};
  background: ${({theme}) => theme.colors.surface};
  border-radius: 12px;
  color: ${({theme}) => theme.colors.gray000};
  z-index: ${({theme}) => theme.zIndex.languageChangeButton};
`;

const languageOptionAttrs = () => ({role: 'link'});

const LanguageOption = styled(Link).attrs(languageOptionAttrs)`
  ${flexCenterVertically}
  height: 40px;
  cursor: pointer;
  padding: 0 16px;
  gap: 16px;

  &:hover {
    background: ${({theme}) => theme.colors.gray910};

    img {
      border-color: ${({theme}) => theme.colors.secondaryHover};
      background-color: ${({theme}) => theme.colors.secondaryHover};
    }
  }
`;

const OptionText = styled.div<{bold?: boolean}>`
  flex-grow: 1;
  ${({bold}) => bold && 'font-weight: bold;'}
`;

const StyledCheckedIcon = styled(CheckedIcon)`
  transform: scale(1.2);
`;
// #endregion

export const changeLanguage = (languageKey = DEFAULT_LANGUAGE_KEY) => {
  const culture =
    SupportedLanguages.find(({key: supportedLanguageKey}) => supportedLanguageKey === languageKey)?.culture ||
    DEFAULT_CULTURE;

  setCurrentLanguageCookie({culture, languageKey});
  // eslint-disable-next-line no-restricted-properties
  const {pathname, search} = window.location || '';
  // eslint-disable-next-line no-restricted-properties
  let fullPathname = pathname;
  if (search) {
    fullPathname += search;
  }
  // eslint-disable-next-line no-restricted-properties
  const newUrl = changeLanguagePrefixToUrlOrPath(fullPathname, languageKey);
  // eslint-disable-next-line no-restricted-properties
  window.location.replace(newUrl);
};

const LanguageSelector = ({className = '', variant = 'tenbis'}: {
  className?: string;
  variant?: Variant;
}) => {
  const {t, currentLanguageKey} = getLocalizationService();
  const [isOpen, setIsOpen] = useState(false);
  usePreventBodyScrolling({shouldPreventScroll: isOpen});
  const currentLanguageObj = useMemo(
    () => SupportedLanguages.find(({key}) => key === currentLanguageKey) || EMPTY_OBJECT,
    [currentLanguageKey],
  );

  const onDropdownClickOutside = useCallback((e: Event) => {
    if (!isOpen) {
      return;
    }

    e.stopPropagation();
    setIsOpen(false);
  }, [isOpen]);

  const handleLanguageSelection = useCallback(
    (languageKey = DEFAULT_LANGUAGE_KEY) => {
      if (currentLanguageObj.key === languageKey) {
        setIsOpen(false);
        return;
      }
      setIsOpen(false);

      changeLanguage(languageKey);
    },
    [currentLanguageObj.key],
  );

  return (
    <Root className={className}>
      <ChangeLangButton
        variant={variant}
        isOpen={isOpen}
        onClick={() => setIsOpen(s => !s)}
        isExpandable
        aria-controls="header-language-dropdown"
        aria-label={t('change_language')}
      >
        <CircleFlag
          variant={variant}
          src={PIE_COUNTRY_ICONS[currentLanguageObj.icon]}
          alt={currentLanguageObj.name}
          isOpen={isOpen}
        />
      </ChangeLangButton>

      <Dropdown isOpen={isOpen} onClickOutside={onDropdownClickOutside} id="header-language-dropdown">
        {SupportedLanguages.map(({key, name, icon}) => (
          <LanguageOption key={key} onClick={() => handleLanguageSelection(key)}>
            <CircleFlag
              variant={variant}
              src={PIE_COUNTRY_ICONS[icon]}
              noAlt
            />
            <OptionText bold={currentLanguageKey === key}>{name}</OptionText>
            {currentLanguageKey === key && <StyledCheckedIcon />}
          </LanguageOption>
        ))}
      </Dropdown>
    </Root>
  );
};

export default memo(LanguageSelector);
